import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Customer, CustomerVehicle } from "@/types";

const VEHICLE_COLUMNS =
  "id, customer_id, brand, model, year, plate, vin, color, vehicle_type, mileage, notes, created_at";

export interface WorkshopData {
  vehicles: CustomerVehicle[];
  customers: Customer[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useWorkshopData(): WorkshopData {
  const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [vehiclesRes, customersRes] = await Promise.all([
        supabase.from("customer_vehicles").select(VEHICLE_COLUMNS).order("created_at", { ascending: false }),
        supabase.from("customers").select("id, first_name, last_name, phone"),
      ]);

      if (vehiclesRes.error) {
        setError("No se pudieron cargar los vehículos.");
        setVehicles([]);
        setCustomers([]);
        return;
      }

      const customerList = (customersRes.data ?? []) as Customer[];
      const customerMap = new Map(
        customerList.map((c) => [c.id, `${c.first_name} ${c.last_name}`.trim()]),
      );
      const phoneMap = new Map(customerList.map((c) => [c.id, c.phone]));

      const enriched = ((vehiclesRes.data ?? []) as CustomerVehicle[]).map((vehicle) => ({
        ...vehicle,
        customer_name: customerMap.get(vehicle.customer_id) ?? "Cliente eliminado",
        customer_phone: phoneMap.get(vehicle.customer_id) ?? null,
      }));

      setVehicles(enriched);
      setCustomers(customerList);
    } catch {
      setError("No se pudieron cargar los vehículos.");
      setVehicles([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { vehicles, customers, loading, error, reload: load };
}